import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { SearchablePlan } from '@/api/planSearch'
import type { PlanItem } from '@/api/planItems'

export type PlanCreationMethod = 'blank' | 'copy' | 'historicalData' | null
export type NameValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid'
export type HistoricalDataSourceType = 'plan' | 'file' | null
export type ProcessingStatus =
  | 'idle'
  | 'uploading'
  | 'creating'
  | 'processing'
  | 'completed'
  | 'error'

interface PlanWizardState {
  isOpen: boolean
  currentStep: number
  selectedMethod: PlanCreationMethod

  // Hierarchy selection for plan creation
  selectedHierarchyId: string | null // hierarchy_id for backend (subclass level)
  planName: string

  // For cascading dropdowns (UI state only)
  selectedDepartmentId: string | null
  selectedClassId: string | null

  // Name validation state
  nameValidationStatus: NameValidationStatus
  nameValidationError: string | null

  // Copy flow state
  selectedSourcePlan: SearchablePlan | null
  showPlanPreview: boolean
  sourceVersionId: string | null

  // Historical data flow state
  historicalDataSourceType: HistoricalDataSourceType
  uploadedFileName: string | null
  historicalDataItems: PlanItem[] | null
  historicalDataTotalCount: number
  showHistoricalDataReview: boolean
  historicalSourcePlan: SearchablePlan | null
  productListId: string | null
  processingStatus: ProcessingStatus
  processingProgress: number

  // Actions
  openWizard: () => void
  closeWizard: () => void
  setMethod: (method: PlanCreationMethod) => void
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: number) => void
  resetWizard: () => void

  // Hierarchy selection actions
  setDepartment: (id: string | null) => void
  setClass: (id: string | null) => void
  setSubclass: (id: string | null) => void
  setPlanName: (name: string) => void
  setNameValidationStatus: (
    status: NameValidationStatus,
    errorMessage?: string | null
  ) => void

  // Copy flow actions
  setSelectedSourcePlan: (plan: SearchablePlan | null) => void
  setSourceVersionId: (id: string | null) => void
  clearSourceSelection: () => void

  // Historical data flow actions
  setHistoricalDataSourceType: (type: HistoricalDataSourceType) => void
  setUploadedFile: (fileName: string | null) => void
  setHistoricalDataItems: (items: PlanItem[] | null, totalCount: number) => void
  setShowHistoricalDataReview: (show: boolean) => void
  setHistoricalSourcePlan: (plan: SearchablePlan | null) => void
  setProductListId: (id: string | null) => void
  setProcessingStatus: (status: ProcessingStatus, progress?: number) => void
  resetHistoricalDataSource: () => void
}

const initialState = {
  isOpen: false,
  currentStep: 0,
  selectedMethod: null as PlanCreationMethod,

  // Hierarchy selection for plan creation
  selectedHierarchyId: null as string | null,
  planName: '',

  // For cascading dropdowns
  selectedDepartmentId: null as string | null,
  selectedClassId: null as string | null,

  // Name validation state
  nameValidationStatus: 'idle' as NameValidationStatus,
  nameValidationError: null as string | null,

  // Copy flow state
  selectedSourcePlan: null as SearchablePlan | null,
  showPlanPreview: false,
  sourceVersionId: null as string | null,

  // Historical data flow state
  historicalDataSourceType: null as HistoricalDataSourceType,
  uploadedFileName: null as string | null,
  historicalDataItems: null as PlanItem[] | null,
  historicalDataTotalCount: 0,
  showHistoricalDataReview: false,
  historicalSourcePlan: null as SearchablePlan | null,
  productListId: null as string | null,
  processingStatus: 'idle' as ProcessingStatus,
  processingProgress: 0,
}

export const usePlanWizardStore = create<PlanWizardState>()(
  devtools(
    set => ({
      ...initialState,

      openWizard: () => set({ isOpen: true }),

      closeWizard: () => set({ isOpen: false }),

      setMethod: (method: PlanCreationMethod) =>
        set({ selectedMethod: method }),

      nextStep: () => set(state => ({ currentStep: state.currentStep + 1 })),

      prevStep: () =>
        set(state => ({
          currentStep: Math.max(0, state.currentStep - 1),
        })),

      goToStep: (step: number) => set({ currentStep: step }),

      resetWizard: () => set(initialState),

      // Hierarchy selection actions
      setDepartment: (id: string | null) =>
        set({
          selectedDepartmentId: id,
          // Clear dependent selections
          selectedClassId: null,
          selectedHierarchyId: null,
        }),

      setClass: (id: string | null) =>
        set({
          selectedClassId: id,
          // Clear dependent selection
          selectedHierarchyId: null,
        }),

      setSubclass: (id: string | null) => set({ selectedHierarchyId: id }),

      setPlanName: (name: string) =>
        set(state => {
          // Only reset validation status if name actually changed
          if (state.planName === name) {
            return {}
          }
          return {
            planName: name,
            nameValidationStatus: 'idle',
            nameValidationError: null,
          }
        }),

      setNameValidationStatus: (
        status: NameValidationStatus,
        errorMessage?: string | null
      ) =>
        set({
          nameValidationStatus: status,
          nameValidationError: errorMessage ?? null,
        }),

      // Copy flow actions
      setSelectedSourcePlan: (plan: SearchablePlan | null) =>
        set({
          selectedSourcePlan: plan,
          showPlanPreview: !!plan,
        }),

      setSourceVersionId: (id: string | null) => set({ sourceVersionId: id }),

      clearSourceSelection: () =>
        set({
          selectedSourcePlan: null,
          showPlanPreview: false,
          sourceVersionId: null,
        }),

      // Historical data flow actions
      setHistoricalDataSourceType: (type: HistoricalDataSourceType) =>
        set({ historicalDataSourceType: type }),

      setUploadedFile: (fileName: string | null) =>
        set({ uploadedFileName: fileName }),

      setHistoricalDataItems: (items: PlanItem[] | null, totalCount: number) =>
        set({
          historicalDataItems: items,
          historicalDataTotalCount: totalCount,
          showHistoricalDataReview: items !== null && items.length > 0,
        }),

      setShowHistoricalDataReview: (show: boolean) =>
        set({ showHistoricalDataReview: show }),

      setHistoricalSourcePlan: (plan: SearchablePlan | null) =>
        set({ historicalSourcePlan: plan }),

      setProductListId: (id: string | null) => set({ productListId: id }),

      setProcessingStatus: (status: ProcessingStatus, progress?: number) =>
        set(state => ({
          processingStatus: status,
          processingProgress: progress ?? state.processingProgress,
        })),

      resetHistoricalDataSource: () =>
        set({
          historicalDataSourceType: null,
          uploadedFileName: null,
          historicalDataItems: null,
          historicalDataTotalCount: 0,
          showHistoricalDataReview: false,
          historicalSourcePlan: null,
          productListId: null,
          processingStatus: 'idle',
          processingProgress: 0,
        }),
    }),
    { name: 'plan-wizard-storage' }
  )
)
